<template>
  <drag-resize v-outside-click="hide" :drag-box-head-style="dragBoxHeadStyle" :drag-box-style="dragBoxStyle">
    <template #header>
      <span>{{ $t("goTo.text") }} </span>
      <i class="icon-close" @click="hide"></i>
    </template>

    <template #body>
      <div class="kk__goto--content">
        <div class="kk__goto--header">
          <div
            class="kk__goto--header-item"
            :class="{
              'kk__goto--header-item-active': activeTab === type.cell,
            }"
            @click="navigate(type.cell)"
          >
            <span>{{ $t("goTo.gotocell.label") }}</span>
          </div>
          <div
            class="kk__goto--header-item"
            :class="{
              'kk__goto--header-item-active': activeTab === type.special,
            }"
            @click="navigate(type.special)"
          >
            <span>{{ $t("goTo.special.label") }}</span>
          </div>
        </div>
        <div class="kk__goto--body" :style="{ ...borderStyle }">
          <go-to-cell v-if="activeTab === type.cell" @hide="hide" @navigate="navigate" @passKeyUp="setKeyUp" />
          <special v-if="activeTab === type.special" @hide="hide" @navigate="navigate" @passOption="setOption" />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="kk__goto--actions">
        <custom-button
          button-type="button"
          button-class="btn btn-default"
          class="kk__goto--actions__cancel"
          @click="hide"
        >
          {{ $t("actions.cancel") }}
        </custom-button>

        <custom-button
          class="kk__goto--actions__add"
          button-type="button"
          button-class="btn btn-default"
          :disabled="disabled"
          @click="goTo"
        >
          {{ $t("actions.ok") }}
        </custom-button>
      </div>
    </template>
  </drag-resize>
</template>

<script>
import hotkeys from "hotkeys-js";
import DesignerMixin from "../DesignerMixin";
import GoToCell from "./GoToCell.vue";
import Special from "./Special.vue";
import { gotoType, HotkeyScopes } from "../../../utils/constants";
import { icons } from "../../../utils/icons";
import CustomButton from "../../commons/CustomButton.vue";
import { getCellId, rangeLabelToIndex, sortRange } from "../cell/Utils";
import { isCell, isRange } from "../../../utils/util";
import DragResize from "../../commons/DragResize.vue";

export default {
  components: {
    GoToCell,
    Special,
    CustomButton,
    DragResize,
  },
  mixins: [DesignerMixin],
  data() {
    return {
      activeTab: gotoType.cell,
      type: { ...gotoType },
      icons: icons,
      prevScope: null,
      disabled: true,
      inputValue: "",
      tempVar: false,
      keyPressed: null,
    };
  },
  computed: {
    dragBoxHeadStyle() {
      return { marginBottom: "1rem" };
    },
    dragBoxStyle() {
      return { backgroundColor: "#f4f6f9" };
    },
    borderStyle() {
      return this.activeTab === this.type.cell ? { borderRadius: "0px 5px 5px 5px" } : { borderRadius: "5px" };
    },
  },
  methods: {
    setOption(value) {
      this.inputValue = value;
      this.disabled = this.inputValue !== null ? false : true;
      // Temp solution to avoid red page error when one of the special are selected
      this.tempVar = true;
    },

    setKeyUp(inputValue, event) {
      this.inputValue = inputValue;
      if (isCell(this.inputValue) || isRange(this.inputValue)) {
        this.disabled = false;
        // Temp solution to avoid red page error when one of the special are selected
        this.tempVar = false;
        if (event.key === "Enter") this.goTo(event);
      } else {
        this.disabled = true;
      }
    },
    goTo(event) {
      event?.preventDefault();
      // Temp solution to avoid red page error when one of the special are selected
      if (this.tempVar) return;
      const range = sortRange(rangeLabelToIndex(this.inputValue));
      if (!range) return;
      if (range[2] >= this.columns.length || range[3] >= this.rows.length) return;
      const activeCellId = getCellId(this.rows[range[1]]?.id, this.columns[range[0]]?.id);
      this.$store.dispatch("cells/setFormulaRange", { title: this.inputValue, activeCellId });
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [range[0], range[1]]);
      this.$store.dispatch("setScope", HotkeyScopes.ProcessDesigner);
      this.$store.dispatch("cells/setShouldScroll");
      this.hide();
    },
    hide() {
      this.$store.dispatch("pdesign/setGotoBox", false);
    },
    navigate(type) {
      const { cell, special } = this.type;
      switch (type) {
        case cell:
          this.activeTab = cell;
          break;
        case special:
          this.activeTab = special;
          break;
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import "../../../assets/styles/variables.less";

.icon-close {
  cursor: pointer;
}

.kk__goto {
  &--content {
    display: flex;
    height: 100%;
  }

  &--header {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    font-size: 1rem;
    background: #f4f6f9;

    &-item {
      color: @kk-color-primary-blue;
      cursor: pointer;
      padding: 0.8rem;
      font-weight: 500;
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
      border-top-left-radius: 3px;
      border-bottom-left-radius: 3px;

      &-active {
        background-color: @kk-color-white;
        color: @kk-color-royal-blue;
        cursor: pointer;
      }

      &:hover:not(&-active) {
        background-color: #d3e3f9;
      }
    }
  }
  &--body {
    flex: 1;
    font-size: 0.9rem;
    background-color: @kk-color-white;
    padding: 22px;
    border: none;
    outline: none;
    padding-right: 3px;
  }

  &--actions {
    display: flex;
    justify-content: end;
    gap: 2rem;

    button {
      width: 130px;
      text-align: center;
      border-radius: 3px;
    }

    &__cancel {
      background: rgba(196, 206, 221, 0.3);
      color: @kk-color-primary-blue;
    }

    &__add {
      background: @kk-color-royal-blue;
      color: @kk-color-white;
    }
  }
}
</style>
